import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Swords, Skull, User, Dices, RefreshCw, Plus, Trash2, Play, Settings, Trophy, Copy, Save, FolderOpen, Zap, ChevronDown, ChevronUp, Cpu } from "lucide-react";
import { toast } from "sonner";
import { rollFoe, rollFoeFromTemplate, FOE_TEMPLATES } from "@/lib/bestiary";
import { rollBroken } from "@/lib/character";
import {
  resolveAttack, rollInitiative, rollMorale,
  punkToCombatant, foeToCombatant, isAutofire, isStealthy,
} from "@/lib/combat";
import { loadLocalEncounters, saveLocalEncounter, deleteLocalEncounter, loadLocalFoes } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Small badge component: bright "SPRAY" tag on autofire weapons.
function SprayBadge() {
  return (
    <span
      data-testid="autofire-badge"
      title="AUTOFIRE — roll damage twice, keep highest"
      className="inline-flex items-center gap-0.5 bg-[color:var(--accent-magenta)] text-[color:var(--bg-void)] text-[8px] font-black uppercase tracking-widest px-1 py-0.5 ml-1 shadow-[2px_2px_0_var(--border-hard)]"
    >
      <Zap size={9} strokeWidth={3} /> SPRAY
    </span>
  );
}

const STAT_KEYS = [
  { k: "strength", label: "STR", hint: "melee" },
  { k: "presence", label: "PRE", hint: "ranged" },
  { k: "knowledge", label: "KNO", hint: "hack/nano" },
  { k: "agility", label: "AGI", hint: "finesse" },
  { k: "toughness", label: "TOU", hint: "raw" },
];

function suggestedAttrFor(weapon) {
  if (!weapon) return "strength";
  if (weapon.type === "nano") return "presence";
  if (weapon.type === "cyber") return "knowledge";
  const lower = (weapon.name || "").toLowerCase();
  if (/rifle|gun|revolver|shotgun|smartgun|pulse|launcher|crossbow|blaster/.test(lower)) return "presence";
  return "strength";
}

function HPBar({ hp, max, broken }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (hp / max) * 100)) : 0;
  const color = broken
    ? "var(--accent-magenta)"
    : hp / max <= 0.33
    ? "var(--accent-magenta)"
    : hp / max <= 0.66
    ? "var(--accent-yellow)"
    : "var(--accent-cyan)";
  return (
    <div className="w-full h-2 border-2 border-[color:var(--border-hard)] relative">
      <div className="absolute inset-0" style={{ background: color, width: `${pct}%` }} />
    </div>
  );
}

// ============================================================
// COMBATANT CARD
// ============================================================
function CombatantCard({ c, active, onRemove, onSelectTarget, canBeTarget, targeted }) {
  const border = c.side === "punk"
    ? "panel-cyan"
    : "panel-magenta";
  return (
    <div
      data-testid={`combatant-${c.id}`}
      className={`panel ${border} p-3 ${active ? "ring-2 ring-[color:var(--accent-yellow)]" : ""} ${targeted ? "outline outline-2 outline-[color:var(--accent-magenta)]" : ""} ${c.hp_current === 0 ? "opacity-50" : ""} transition-all`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
            {c.side === "punk" ? "PUNK" : `FOE · ${c.type || ""}`}{active ? " · ACTIVE" : ""}
          </div>
          <div className="font-display text-lg font-black uppercase tracking-tight truncate">
            {c.name}
          </div>
        </div>
        {onRemove && (
          <button
            onClick={() => onRemove(c.id)}
            className="p-1 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
            aria-label="Remove"
            data-testid={`remove-${c.id}`}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="my-2">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
          <span>HP</span>
          <span className={c.hp_current === 0 ? "text-[color:var(--accent-magenta)] font-black" : "text-[color:var(--accent-yellow)] font-black"}>
            {c.hp_current}/{c.hp_max}
          </span>
        </div>
        <HPBar hp={c.hp_current} max={c.hp_max} broken={c.hp_current === 0} />
      </div>

      <div className="grid grid-cols-5 gap-1 text-center mb-2">
        {STAT_KEYS.map((s) => (
          <div key={s.k} className="border border-[color:var(--border-hard)] py-0.5">
            <div className="text-[8px] font-mono uppercase text-[color:var(--text-muted)]">{s.label}</div>
            <div className="font-display text-sm text-[color:var(--accent-yellow)]">
              {(c.stats[s.k] >= 0 ? "+" : "") + (c.stats[s.k] || 0)}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
        <span>ARMOR {c.armor > 0 ? `−d${c.armor}` : "0"}</span>
        <span>MORALE {c.morale}</span>
        {c.initiative > 0 && <span className="text-[color:var(--accent-cyan)]">INIT {c.initiative}</span>}
      </div>

      {canBeTarget && (
        <button
          data-testid={`target-${c.id}`}
          onClick={() => onSelectTarget(c.id)}
          className="cy-btn cy-btn-magenta text-[10px] w-full mt-2 py-1"
        >
          TARGET
        </button>
      )}
    </div>
  );
}

// ============================================================
// MAIN ARENA
// ============================================================
export default function CombatArena({ open, onClose, roster }) {
  const { user } = useAuth();

  // Phases: 'setup' | 'combat'
  const [phase, setPhase] = useState("setup");

  // GM settings
  const [dr, setDr] = useState(12);
  const [mode, setMode] = useState("strict"); // 'strict' | 'house'

  // Combatants
  const [combatants, setCombatants] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [round, setRound] = useState(1);

  // Attack in-progress state
  const [targetId, setTargetId] = useState(null);
  const [weaponIdx, setWeaponIdx] = useState(0);
  const [attrKey, setAttrKey] = useState("strength");
  const [useAutofire, setUseAutofire] = useState(true);
  const [shotsThisTurn, setShotsThisTurn] = useState(0); // 0..3 autofire shots fired this turn
  const [reinforcements, setReinforcements] = useState([]); // [{ id, round, count, caller }]
  const [nextAdvantage, setNextAdvantage] = useState(false);  // one-shot ADV from glitch/gear

  // Log
  const [log, setLog] = useState([]);

  // Saved encounters + custom foes
  const [savedEncounters, setSavedEncounters] = useState([]);
  const [customFoes, setCustomFoes] = useState([]);
  const [encounterId, setEncounterId] = useState(null);
  const [encounterName, setEncounterName] = useState("");
  const [showLoad, setShowLoad] = useState(false);

  // Sync-back after combat
  const [syncHp, setSyncHp] = useState(true);
  const [syncGlitches, setSyncGlitches] = useState(true);
  const [syncDone, setSyncDone] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Collapsible combat log — starts collapsed on mobile, expanded on desktop.
  const [logCollapsed, setLogCollapsed] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );

  useEffect(() => {
    if (!open) {
      // reset on close
      setPhase("setup");
      setCombatants([]);
      setActiveIdx(0);
      setRound(1);
      setTargetId(null);
      setWeaponIdx(0);
      setLog([]);
      setEncounterId(null);
      setEncounterName("");
      setShowLoad(false);
      setSyncDone(false);
    } else {
      // fetch saved encounters + custom foes
      (async () => {
        if (user) {
          try {
            const r = await api.get("/encounters");
            setSavedEncounters(r.data || []);
          } catch { setSavedEncounters(loadLocalEncounters()); }
          try {
            const rf = await api.get("/foes");
            setCustomFoes(rf.data || []);
          } catch { setCustomFoes(loadLocalFoes()); }
        } else {
          setSavedEncounters(loadLocalEncounters());
          setCustomFoes(loadLocalFoes());
        }
      })();
    }
  }, [open, user]);

  // -------------------- SETUP --------------------
  const addPunk = (char) => {
    if (combatants.find((c) => c.id === char.id)) {
      toast("ALREADY IN THE FIGHT", { description: char.name });
      return;
    }
    setCombatants((cs) => [...cs, punkToCombatant(char)]);
  };
  const addFreshFoe = (choice = null, count = 1) => {
    // choice can be: null (random), a numeric built-in index, or an object { custom: <template> }
    setCombatants((cs) => {
      const next = [...cs];
      for (let i = 0; i < count; i++) {
        if (choice && typeof choice === "object" && choice.custom) {
          next.push(foeToCombatant(rollFoeFromTemplate(choice.custom)));
        } else if (typeof choice === "number") {
          next.push(foeToCombatant(rollFoe(choice)));
        } else {
          next.push(foeToCombatant(rollFoe()));
        }
      }
      return next;
    });
  };
  const removeCombatant = (id) => setCombatants((cs) => cs.filter((c) => c.id !== id));

  const startCombat = () => {
    if (combatants.length < 2) {
      toast.error("NEED AT LEAST 2 COMBATANTS");
      return;
    }
    // Roll initiative for everyone, then promote any stealthy foe to the front (ambush).
    const rolled = combatants
      .map((c) => ({ ...c, initiative: rollInitiative(c).total }))
      .sort((a, b) => b.initiative - a.initiative);
    const stealthy = rolled.filter(isStealthy);
    const rest = rolled.filter((c) => !isStealthy(c));
    const withInit = [...stealthy, ...rest];
    setCombatants(withInit);
    setActiveIdx(0);
    setRound(1);
    setPhase("combat");
    const entries = [{ kind: "system", text: `[INIT] Order: ${withInit.map((c) => `${c.name}(${c.initiative}${isStealthy(c) ? " · AMBUSH" : ""})`).join(" > ")}` }];
    if (stealthy.length) {
      entries.push({
        kind: "system",
        text: `[AMBUSH] ${stealthy.map((c) => c.name).join(", ")} strike first — surprise round. First attack rolls with ADVANTAGE.`,
      });
    }
    setLog(entries);
    // If a stealthy foe leads, bank ADV on their first attack
    if (stealthy.length) setNextAdvantage(true);
  };

  // -------------------- COMBAT --------------------
  const active = combatants[activeIdx];
  const target = combatants.find((c) => c.id === targetId);
  const weapon = active?.weapons?.[weaponIdx];

  useEffect(() => {
    // reset target/weapon when active changes
    setTargetId(null);
    setWeaponIdx(0);
    setShotsThisTurn(0);
    if (active?.weapons?.[0]) setAttrKey(suggestedAttrFor(active.weapons[0]));
  }, [activeIdx, phase]);

  useEffect(() => {
    if (weapon) setAttrKey(suggestedAttrFor(weapon));
  }, [weaponIdx, activeIdx]);

  const nextTurn = () => {
    let next = activeIdx + 1;
    let nextRound = round;
    // skip broken combatants
    for (let i = 0; i < combatants.length; i++) {
      if (next >= combatants.length) { next = 0; nextRound += 1; }
      if (combatants[next].hp_current > 0) break;
      next += 1;
    }
    setActiveIdx(next);
    if (nextRound !== round) {
      setRound(nextRound);
      setLog((l) => [...l, { kind: "system", text: `[ROUND ${nextRound}]` }]);
      const arriving = reinforcements.filter((r) => r.round <= nextRound);
      if (arriving.length) {
        const secops = FOE_TEMPLATES.filter((t) => /^SECOP/.test(t.name));
        const fresh = arriving.flatMap((r) => Array.from({ length: r.count }, () =>
          foeToCombatant(rollFoeFromTemplate(secops[Math.floor(Math.random() * secops.length)]))));
        setCombatants((cs) => [...cs, ...fresh]);
        setReinforcements((rs) => rs.filter((r) => r.round > nextRound));
        setLog((l) => [...l, { kind: "system", text: `[SECOPS ARRIVE] ${fresh.length} SecOps storm in: ${fresh.map((f) => f.name).join(", ")}.` }]);
        toast.error(`${fresh.length} SECOPS ARRIVE`);
      }
    }
  };

  const executeAttack = () => {
    if (!active || !target || !weapon) {
      toast.error("PICK A TARGET AND WEAPON");
      return;
    }
    if (weapon.app && !active.jackedIn) {
      toast.error("JACK IN FIRST", { description: "Apps need a live cyberdeck link." });
      return;
    }
    if (target.hp_current === 0) {
      toast.error("TARGET IS ALREADY DOWN");
      return;
    }

    const isAF = isAutofire(weapon.damage) && useAutofire;
    // DR ramps by 1 per shot fired earlier in the same turn.
    const shotDR = isAF ? dr + shotsThisTurn : dr;
    const shotIndex = isAF ? shotsThisTurn + 1 : 1;
    const shotsTotal = isAF ? 3 : 1;

    const r = resolveAttack({
      attacker: active,
      target,
      weapon,
      attribute: attrKey,
      dr: shotDR,
      mode,
      useAutofire: false,      // interactive per-shot mode; no "roll damage twice" behaviour
      advantage: nextAdvantage || (weapon.app && active.appAdvantage),
    });
    if (weapon.app && active.appAdvantage) {
      setCombatants((cs) => cs.map((c) => c.id === active.id ? { ...c, appAdvantage: false } : c));
    }
    r.shotIndex = shotIndex;
    r.shotsTotal = shotsTotal;
    if (nextAdvantage) setNextAdvantage(false);

    // Apply HP change
    setCombatants((cs) => cs.map((c) => c.id === target.id ? { ...c, hp_current: r.hp_after } : c));
    // Log line
    const line = formatLogLine(r);
    setLog((l) => [...l, { kind: r.outcome, text: line, result: r }]);
    toast(line.split("\n")[0]);

    // Post-attack processing
    if (r.broken && target.side === "foe") {
      const alive = combatants.filter((c) => c.side === "foe" && c.id !== target.id && c.hp_current > 0);
      alive.forEach((f) => {
        const m = rollMorale(f, dr);
        setLog((l) => [...l, {
          kind: "morale",
          text: `[MORALE] ${f.name} d20=${m.nat}+PRE${m.mod >= 0 ? "+" : ""}${m.mod} = ${m.total} vs DR${m.dr} — ${m.pass ? "HOLDS" : "FLEES"}`,
        }]);
        if (!m.pass) {
          setCombatants((cs) => cs.map((c) => c.id === f.id ? { ...c, hp_current: 0, _fled: true } : c));
        }
      });
    }
    if (r.broken && target.side === "punk") {
      const b = rollBroken();
      setLog((l) => [...l, {
        kind: "broken",
        text: `[BROKEN] ${target.name} at 0 HP — d8=${b.roll}  ⟶  ${b.effect}`,
      }]);
    }

    // Decide whether the turn continues (autofire burst) or ends.
    const canBurstAgain = isAF && shotIndex < 3 && r.outcome !== "fumble" && r.hp_after > 0;
    if (canBurstAgain) {
      setShotsThisTurn(shotIndex);
      // Stay on the same combatant; DO NOT advance turn.
    } else {
      setShotsThisTurn(0);
      setTimeout(() => nextTurn(), 300);
    }
  };

  // Manually end the burst / turn — used when a punk chooses not to fire another autofire shot.
  const endTurn = () => {
    setShotsThisTurn(0);
    setLog((l) => [...l, { kind: "system", text: `[END TURN] ${active.name} holds fire.` }]);
    nextTurn();
  };

  const jackIn = () => {
    if (!active?.canJackIn || active.jackedIn) return;
    const nat = Math.floor(Math.random() * 20) + 1;
    const mod = active.stats?.knowledge || 0;
    const total = nat + mod;
    const roll = `d20=${nat}+KNO${mod >= 0 ? "+" : ""}${mod} = ${total} vs DR${dr}`;
    if (nat !== 1 && (nat === 20 || total >= dr)) {
      const crit = nat === 20;
      setCombatants((cs) => cs.map((c) => c.id === active.id ? { ...c, jackedIn: true, appAdvantage: crit } : c));
      setLog((l) => [...l, { kind: "system", text: `[JACK IN] ${active.name} ${roll} — SUCCESS. Apps online for the rest of the fight.${crit ? " NAT 20: first app attack has ADVANTAGE." : ""}` }]);
      toast.success(crit ? "CLEAN JACK-IN · ADV ON FIRST APP" : "JACKED IN", { description: roll });
    } else {
      const count = Math.floor(Math.random() * 6) + 1;
      const delay = Math.floor(Math.random() * 4) + 1;
      setReinforcements((rs) => [...rs, { id: `rf_${Date.now()}`, round: round + delay, count, caller: active.name }]);
      setLog((l) => [...l, { kind: "fumble", text: `[JACK IN FAILED] ${active.name} ${roll} — ICE tripped! d6=${count} SecOps alerted, arriving in d4=${delay} round${delay > 1 ? "s" : ""} (round ${round + delay}).` }]);
      toast.error("JACK-IN FAILED · SECOPS ALERTED", { description: `${count} SecOps arrive in ${delay} round${delay > 1 ? "s" : ""}` });
    }
    nextTurn();
  };

  const passTurn = () => {
    setLog((l) => [...l, { kind: "system", text: `[PASS] ${active.name} takes cover / waits.` }]);
    nextTurn();
  };

  const editHp = (id, delta) => {
    setCombatants((cs) => cs.map((c) => c.id === id ? { ...c, hp_current: Math.max(0, Math.min(c.hp_max, c.hp_current + delta)) } : c));
  };

  // Spend one glitch from the active punk → next attack has ADVANTAGE.
  const useGlitch = () => {
    if (!active || active.side !== "punk") return;
    if ((active.glitches_current || 0) <= 0) {
      toast.error("NO GLITCHES LEFT");
      return;
    }
    setCombatants((cs) => cs.map((c) => c.id === active.id
      ? { ...c, glitches_current: Math.max(0, (c.glitches_current || 0) - 1) }
      : c
    ));
    setNextAdvantage(true);
    setLog((l) => [...l, { kind: "system", text: `[GLITCH] ${active.name} spent 1 GLITCH — next attack rolls d20 with ADVANTAGE.` }]);
    toast.success("GLITCH SPENT — ADV on next attack");
  };

  // Use a gear item off the active punk (heal / advantage / reroll).
  const useItem = (item) => {
    if (!active || active.side !== "punk" || !item) return;
    const eff = item.effect;
    if (eff.kind === "heal") {
      // parse the item die (e.g. "d4") and roll it
      const m = String(eff.die).match(/(\d*)d(\d+)([+-]\d+)?/i);
      let healed = 0;
      if (m) {
        const c = m[1] ? parseInt(m[1], 10) : 1;
        const s = parseInt(m[2], 10);
        const mod = m[3] ? parseInt(m[3], 10) : 0;
        for (let i = 0; i < c; i++) healed += Math.floor(Math.random() * s) + 1;
        healed += mod;
      } else {
        healed = 1;
      }
      const before = active.hp_current;
      const after = Math.min(active.hp_max, before + healed);
      setCombatants((cs) => cs.map((c) => c.id === active.id ? { ...c, hp_current: after } : c));
      setLog((l) => [...l, { kind: "system", text: `[ITEM] ${active.name} used ${item.name} — healed ${after - before} HP (${before} → ${after}).` }]);
      toast.success(`HEALED +${after - before} HP`);
    } else if (eff.kind === "advantage") {
      setNextAdvantage(true);
      setLog((l) => [...l, { kind: "system", text: `[ITEM] ${active.name} used ${item.name} — next attack has ADVANTAGE.` }]);
      toast.success("ADV NEXT ATTACK");
    } else if (eff.kind === "reroll") {
      setNextAdvantage(true);
      setLog((l) => [...l, { kind: "system", text: `[ITEM] ${active.name} used ${item.name} — next attack rolls with ADVANTAGE.` }]);
      toast.success("REROLL BANKED (ADV NEXT ATTACK)");
    }
    // Consume: remove that item from combatant's usableGear so it can't be re-used
    setCombatants((cs) => cs.map((c) => c.id === active.id
      ? { ...c, usableGear: (c.usableGear || []).filter((g) => g.id !== item.id) }
      : c
    ));
  };

  // Winner check
  const winner = useMemo(() => {
    if (phase !== "combat") return null;
    const punksAlive = combatants.filter((c) => c.side === "punk" && c.hp_current > 0).length;
    const foesAlive = combatants.filter((c) => c.side === "foe" && c.hp_current > 0).length;
    if (punksAlive === 0 && combatants.some((c) => c.side === "punk")) return "foes";
    if (foesAlive === 0 && combatants.some((c) => c.side === "foe") && reinforcements.length === 0) return "punks";
    return null;
  }, [combatants, phase, reinforcements]);

  const copyLog = async () => {
    const text = log.map((l) => l.text).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("COMBAT LOG COPIED");
    } catch {
      toast.error("COPY FAILED");
    }
  };

  const reset = () => {
    setPhase("setup");
    setCombatants([]);
    setActiveIdx(0);
    setRound(1);
    setLog([]);
    setReinforcements([]);
    setTargetId(null);
    setEncounterId(null);
    setEncounterName("");
    setSyncDone(false);
  };

  // -------------------- SYNC PUNKS BACK TO SHEET --------------------
  // Compute per-punk delta (hp / glitches) and let the GM write it back.
  const punkChanges = useMemo(() => {
    return combatants
      .filter((c) => c.side === "punk")
      .map((c) => {
        const original = (roster || []).find((r) => r.id === c.id) || {};
        const hpDelta = (c.hp_current || 0) - (original.hp_current ?? c.hp_max);
        const glitchDelta = (c.glitches_current || 0) - (original.glitches_count ?? c.glitches_max);
        return {
          id: c.id,
          name: c.name,
          hp_current: c.hp_current,
          hp_original: original.hp_current ?? c.hp_max,
          glitches_current: c.glitches_current || 0,
          glitches_original: original.glitches_count ?? c.glitches_max,
          hpDelta,
          glitchDelta,
          changed: hpDelta !== 0 || glitchDelta !== 0,
        };
      });
  }, [combatants, roster]);

  const anyPunkChanged = punkChanges.some((p) => p.changed);

  const syncPunks = async (opts = null) => {
    const doHp = opts ? !!opts.hp : syncHp;
    const doGlitches = opts ? !!opts.glitches : syncGlitches;
    setSyncing(true);
    let ok = 0, fail = 0;
    for (const p of punkChanges) {
      if (!p.changed) continue;
      const patch = {};
      if (doHp) patch.hp_current = p.hp_current;
      if (doGlitches) patch.glitches_count = p.glitches_current;
      if (Object.keys(patch).length === 0) continue;
      try {
        if (user) {
          await api.put(`/characters/${p.id}`, patch);
        } else {
          const original = (roster || []).find((r) => r.id === p.id);
          if (original) {
            const { upsertLocalCharacter } = await import("@/lib/storage");
            upsertLocalCharacter({ ...original, ...patch });
          }
        }
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setSyncing(false);
    setSyncDone(true);
    if (fail === 0 && ok > 0) toast.success(`SYNCED ${ok} SHEET${ok === 1 ? "" : "S"}`);
    else if (fail > 0) toast.error(`SYNC PARTIAL — ${ok} ok, ${fail} failed`);
    return { ok, fail };
  };

  // Wrap close so HP/glitch deltas write back automatically before the arena tears down.
  const handleClose = async () => {
    if (anyPunkChanged && !syncDone) {
      await syncPunks({ hp: true, glitches: true });
    }
    onClose();
  };

  // -------------------- SAVE / LOAD ENCOUNTER --------------------
  const persistEncounter = async (enc) => {
    if (user) {
      try {
        const r = await api.post("/encounters", enc);
        setEncounterId(r.data.id);
        // refresh list
        try { const l = await api.get("/encounters"); setSavedEncounters(l.data || []); } catch {}
        return r.data;
      } catch {
        toast.error("CLOUD SAVE FAILED — keeping local backup");
      }
    }
    const saved = saveLocalEncounter({ ...enc, id: enc.id || `enc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` });
    setEncounterId(saved.id);
    setSavedEncounters(loadLocalEncounters());
    return saved;
  };

  const handleSave = async () => {
    const name = (encounterName || `Encounter ${new Date().toLocaleString()}`).trim();
    const payload = {
      id: encounterId || undefined,
      name,
      combatants,
      round,
      active_idx: activeIdx,
      dr,
      mode,
      log,
    };
    await persistEncounter(payload);
    setEncounterName(name);
    toast.success(`SAVED · ${name}`);
  };

  const handleLoad = (enc) => {
    setEncounterId(enc.id);
    setEncounterName(enc.name || "");
    setCombatants(enc.combatants || []);
    setRound(enc.round || 1);
    setActiveIdx(enc.active_idx || 0);
    setDr(enc.dr || 12);
    setMode(enc.mode || "strict");
    setLog(enc.log || []);
    setPhase((enc.combatants && enc.combatants.length && enc.log && enc.log.length) ? "combat" : "setup");
    setShowLoad(false);
    toast.success(`LOADED · ${enc.name}`);
  };

  const handleDeleteEncounter = async (id) => {
    if (!window.confirm("DELETE THIS ENCOUNTER? PERMANENT.")) return;
    if (user) {
      try { await api.delete(`/encounters/${id}`); } catch { toast.error("Cloud delete failed"); return; }
    } else {
      deleteLocalEncounter(id);
    }
    setSavedEncounters((s) => s.filter((e) => e.id !== id));
    if (encounterId === id) setEncounterId(null);
    toast("ENCOUNTER DELETED");
  };

  const punks = combatants.filter((c) => c.side === "punk");
  const foes = combatants.filter((c) => c.side === "foe");

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="relative panel panel-yellow w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden"
            data-testid="arena-modal"
          >
            {/* HEADER */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
              <div className="flex items-center gap-3">
                <Swords className="text-[color:var(--accent-yellow)]" />
                <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-tighter">
                  COMBAT//ARENA
                </div>
                {phase === "combat" && (
                  <span className="tape tape-magenta text-[10px]">ROUND {round}</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 border-2 border-[color:var(--border-hard)] px-2 py-1">
                  <Settings size={12} className="text-[color:var(--text-muted)]" />
                  <span className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">DR</span>
                  <button data-testid="dr-dec" className="cy-btn text-[10px] min-h-0 px-1 py-0" onClick={() => setDr((d) => Math.max(2, d - 2))}>−</button>
                  <span data-testid="dr-value" className="font-display font-black text-lg text-[color:var(--accent-yellow)] tabular-nums w-6 text-center">{dr}</span>
                  <button data-testid="dr-inc" className="cy-btn text-[10px] min-h-0 px-1 py-0" onClick={() => setDr((d) => Math.min(20, d + 2))}>+</button>
                </div>
                <div className="flex border-2 border-[color:var(--border-hard)]">
                  {["strict", "house"].map((m) => (
                    <button
                      key={m}
                      data-testid={`mode-${m}`}
                      onClick={() => setMode(m)}
                      className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${mode === m ? "bg-[color:var(--accent-cyan)] text-[color:var(--bg-void)]" : "text-[color:var(--text-secondary)]"}`}
                    >{m}</button>
                  ))}
                </div>
                <button
                  data-testid="arena-load-btn"
                  onClick={() => setShowLoad((s) => !s)}
                  className="cy-btn cy-btn-cyan text-[10px] px-2 py-1"
                  title="Load a saved encounter"
                >
                  <FolderOpen size={12} className="mr-1" /> LOAD
                </button>
                <button
                  data-testid="arena-save-btn"
                  onClick={handleSave}
                  disabled={combatants.length === 0}
                  className="cy-btn cy-btn-solid-yellow text-[10px] px-2 py-1 disabled:opacity-50"
                  title="Save this encounter for later"
                >
                  <Save size={12} className="mr-1" /> SAVE
                </button>
                <button
                  data-testid="arena-close"
                  onClick={handleClose}
                  aria-label="Close"
                  className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Load panel (drawer) */}
            {showLoad && (
              <div className="border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)] p-3" data-testid="load-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)]">SAVED ENCOUNTERS</div>
                  <input
                    data-testid="encounter-name-input"
                    type="text"
                    value={encounterName}
                    onChange={(e) => setEncounterName(e.target.value)}
                    placeholder="Name this encounter…"
                    className="cy-input flex-1 max-w-xs ml-2"
                  />
                </div>
                {savedEncounters.length === 0 ? (
                  <div className="font-mono text-xs text-[color:var(--text-muted)] italic">// no saves yet — hit SAVE to bookmark this fight.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                    {savedEncounters.map((enc) => (
                      <div key={enc.id} className="flex items-center justify-between gap-2 border-2 border-[color:var(--border-hard)] px-2 py-1" data-testid={`saved-enc-${enc.id}`}>
                        <button
                          onClick={() => handleLoad(enc)}
                          className="text-left min-w-0 flex-1"
                        >
                          <div className="font-display uppercase font-black text-xs truncate">{enc.name}</div>
                          <div className="text-[9px] font-mono uppercase text-[color:var(--text-muted)]">
                            {enc.combatants?.length || 0} combatants · R{enc.round || 1}
                          </div>
                        </button>
                        <button
                          onClick={() => handleDeleteEncounter(enc.id)}
                          className="p-1 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
                          aria-label="Delete"
                          data-testid={`delete-enc-${enc.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* BODY */}
            <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_320px]">
              {/* MAIN COLUMN */}
              <div className="overflow-y-auto p-3 sm:p-4">
                {phase === "setup" ? (
                  <SetupPhase
                    roster={roster}
                    customFoes={customFoes}
                    combatants={combatants}
                    onAddPunk={addPunk}
                    onAddFoe={addFreshFoe}
                    onRemove={removeCombatant}
                    onStart={startCombat}
                  />
                ) : (
                  <CombatPhase
                    combatants={combatants}
                    punks={punks}
                    foes={foes}
                    active={active}
                    target={target}
                    weapon={weapon}
                    weaponIdx={weaponIdx}
                    attrKey={attrKey}
                    dr={dr}
                    onSelectTarget={setTargetId}
                    onSelectWeapon={setWeaponIdx}
                    onSelectAttr={setAttrKey}
                    useAutofire={useAutofire}
                    onToggleAutofire={setUseAutofire}
                    shotsThisTurn={shotsThisTurn}
                    onEndTurn={endTurn}
                    nextAdvantage={nextAdvantage}
                    onAttack={executeAttack}
                    onUseGlitch={useGlitch}
                    onUseItem={useItem}
                    onPass={passTurn}
                    onJackIn={jackIn}
                    reinforcements={reinforcements}
                    round={round}
                    onEditHp={editHp}
                    winner={winner}
                    onReset={reset}
                    punkChanges={punkChanges}
                    anyPunkChanged={anyPunkChanged}
                    syncHp={syncHp}
                    syncGlitches={syncGlitches}
                    onToggleSyncHp={setSyncHp}
                    onToggleSyncGlitches={setSyncGlitches}
                    onSyncPunks={syncPunks}
                    syncDone={syncDone}
                    syncing={syncing}
                  />
                )}
              </div>

              {/* LOG COLUMN */}
              <div className={`border-t-2 lg:border-t-0 lg:border-l-2 border-[color:var(--border-hard)] bg-[color:var(--bg-void)] flex flex-col ${logCollapsed ? "lg:max-h-16" : ""}`}>
                <div className="flex items-center justify-between px-3 py-2 border-b-2 border-[color:var(--border-hard)]">
                  <button
                    onClick={() => setLogCollapsed((v) => !v)}
                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)] hover:text-[color:var(--accent-yellow)]"
                    data-testid="toggle-log-btn"
                    aria-label="Toggle combat log"
                  >
                    {logCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                    COMBAT LOG
                    <span className="ml-1 text-[color:var(--text-muted)]">({log.length})</span>
                  </button>
                  <button data-testid="copy-log-btn" onClick={copyLog} className="cy-btn cy-btn-cyan text-[10px] min-h-0 px-2 py-1">
                    <Copy size={10} className="mr-1" /> COPY
                  </button>
                </div>
                {logCollapsed ? (
                  <button
                    onClick={() => setLogCollapsed(false)}
                    className="px-3 py-1 text-left text-[10px] font-mono text-[color:var(--text-muted)] italic truncate hover:text-[color:var(--text-primary)]"
                    data-testid="log-preview"
                  >
                    {log.length === 0
                      ? "// waiting for combat…"
                      : `▸ ${(log[log.length - 1]?.text || "").split("\n")[0]}`}
                  </button>
                ) : (
                  <div className="flex-1 overflow-y-auto p-2 space-y-1 text-[11px] font-mono leading-snug max-h-[40vh] lg:max-h-none" data-testid="combat-log">
                    {log.length === 0 && (
                      <div className="text-[color:var(--text-muted)] italic">// waiting for combat…</div>
                    )}
                    {log.map((l, i) => (
                      <div key={i} className={`whitespace-pre-wrap ${
                        l.kind === "crit" ? "text-[color:var(--accent-yellow)] font-bold" :
                        l.kind === "fumble" ? "text-[color:var(--accent-magenta)] font-bold" :
                        l.kind === "hit" ? "text-[color:var(--accent-cyan)]" :
                        l.kind === "dodged" ? "text-[color:var(--text-secondary)]" :
                        l.kind === "miss" ? "text-[color:var(--text-muted)]" :
                        l.kind === "broken" ? "text-[color:var(--accent-magenta)] font-bold" :
                        l.kind === "morale" ? "text-[color:var(--accent-cyan)]" :
                        "text-[color:var(--text-primary)]"
                      }`}>{l.text}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// ============================================================
// SETUP PHASE — pick punks + foes
// ============================================================
function SetupPhase({ roster, customFoes, combatants, onAddPunk, onAddFoe, onRemove, onStart }) {
  const [foeSel, setFoeSel] = useState("random"); // "random" | "b:<idx>" | "c:<id>"
  const [foeQty, setFoeQty] = useState(1);
  const alreadyIn = (id) => combatants.some((c) => c.id === id);

  const handleAddFoe = () => {
    const qty = Math.max(1, Math.min(20, parseInt(foeQty, 10) || 1));
    if (foeSel === "random") return onAddFoe(null, qty);
    if (foeSel.startsWith("b:")) return onAddFoe(parseInt(foeSel.slice(2), 10), qty);
    if (foeSel.startsWith("c:")) {
      const id = foeSel.slice(2);
      const tmpl = (customFoes || []).find((f) => f.id === id);
      if (!tmpl) return;
      onAddFoe({ custom: tmpl }, qty);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Roster picker */}
        <div className="panel panel-cyan p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display uppercase font-black tracking-tight text-lg">
              <User size={14} className="inline mr-1" /> YOUR PUNKS
            </div>
            <span className="tape tape-cyan text-[10px]">{roster?.length || 0}</span>
          </div>
          {(!roster || roster.length === 0) && (
            <div className="font-mono text-xs text-[color:var(--text-muted)] p-2">No punks yet — create one on the Dashboard first.</div>
          )}
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1" data-testid="roster-list">
            {roster?.map((c) => (
              <button
                key={c.id}
                data-testid={`add-punk-${c.id}`}
                onClick={() => onAddPunk(c)}
                disabled={alreadyIn(c.id)}
                className="w-full flex items-center justify-between gap-2 border-2 border-[color:var(--border-hard)] px-2 py-1 hover:border-[color:var(--accent-cyan)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <div className="text-left min-w-0">
                  <div className="font-display uppercase font-black text-sm truncate">{c.name || "UNNAMED"}</div>
                  <div className="text-[9px] font-mono uppercase text-[color:var(--text-muted)]">{c.char_class || "// UNCLASSIFIED"}</div>
                </div>
                <div className="text-[10px] font-mono">HP {c.hp_current}/{c.hp_max}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Foe roller with picker + qty */}
        <div className="panel panel-magenta p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display uppercase font-black tracking-tight text-lg">
              <Skull size={14} className="inline mr-1" /> FOES
            </div>
            <span className="tape tape-magenta text-[10px]">{(customFoes || []).length} SAVED</span>
          </div>
          <div className="grid grid-cols-[1fr_70px_auto] gap-2 items-end">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">TYPE</div>
              <select
                data-testid="foe-type-select"
                value={foeSel}
                onChange={(e) => setFoeSel(e.target.value)}
                className="cy-input mt-1"
              >
                <option value="random">◆ RANDOM ROLL</option>
                {(customFoes || []).length > 0 && (
                  <optgroup label="── YOUR FOES ──">
                    {(customFoes || []).map((f) => (
                      <option key={f.id} value={`c:${f.id}`}>★ {f.name}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="── CORE / FAN ──">
                  {FOE_TEMPLATES.map((t, i) => (
                    <option key={i} value={`b:${i}`}>{t.book ? "◈ " : ""}{t.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">QTY</div>
              <input
                data-testid="foe-qty-input"
                type="number"
                min={1}
                max={20}
                value={foeQty}
                onChange={(e) => setFoeQty(e.target.value)}
                className="cy-input mt-1 text-center"
              />
            </div>
            <button data-testid="add-foe-btn" onClick={handleAddFoe} className="cy-btn cy-btn-magenta text-xs h-[38px]">
              <Plus size={12} className="mr-1" /> ADD
            </button>
          </div>
          <div className="font-mono text-[10px] text-[color:var(--text-muted)] mt-2 uppercase tracking-widest">
            {foeSel === "random"
              ? "Random rolls a mystery statblock each time."
              : foeSel.startsWith("c:")
              ? `Adds ${foeQty} × ${(customFoes || []).find((f) => f.id === foeSel.slice(2))?.name} — your custom template.`
              : `Adds ${foeQty} × ${FOE_TEMPLATES[parseInt(foeSel.slice(2), 10)]?.name} — each with its own fresh HP/stats.`}
          </div>
        </div>
      </div>

      {/* Current arena roster */}
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display uppercase font-black tracking-tight text-lg">// IN THE ARENA</div>
          <button
            data-testid="start-combat-btn"
            onClick={onStart}
            disabled={combatants.length < 2}
            className="cy-btn cy-btn-solid-yellow text-xs disabled:opacity-50"
          >
            <Play size={12} className="mr-1" /> START COMBAT
          </button>
        </div>
        {combatants.length === 0 ? (
          <div className="font-mono text-xs text-[color:var(--text-muted)] p-4 text-center">
            Add at least 1 punk and 1 foe to begin.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {combatants.map((c) => (
              <CombatantCard key={c.id} c={c} onRemove={onRemove} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// COMBAT PHASE — turn-by-turn play
// ============================================================
function CombatPhase({ combatants, punks, foes, active, target, weapon, weaponIdx, attrKey, dr, onSelectTarget, onSelectWeapon, onSelectAttr, useAutofire, onToggleAutofire, shotsThisTurn, onEndTurn, nextAdvantage, onAttack, onUseGlitch, onUseItem, onPass, onJackIn, reinforcements = [], round, onEditHp, winner, onReset, punkChanges, anyPunkChanged, syncHp, syncGlitches, onToggleSyncHp, onToggleSyncGlitches, onSyncPunks, syncDone, syncing }) {
  if (winner) {
    return (
      <div className="panel panel-yellow p-6 space-y-4">
        <div className="text-center space-y-3">
          <Trophy className="mx-auto text-[color:var(--accent-yellow)]" size={64} />
          <div className="font-display uppercase font-black text-4xl">
            {winner === "punks" ? "PUNKS SURVIVE" : "PUNKS FALL"}
          </div>
          <div className="font-mono text-xs text-[color:var(--text-secondary)] uppercase tracking-widest">
            {winner === "punks" ? "Every foe is dead or fled." : "The wasteland claims them all."}
          </div>
        </div>

        {/* Sync back panel */}
        {anyPunkChanged && !syncDone && (
          <div className="panel panel-cyan p-3" data-testid="sync-panel">
            <div className="font-display uppercase font-black text-lg mb-2">// WRITE BACK TO SHEETS?</div>
            <div className="font-mono text-xs text-[color:var(--text-secondary)] mb-3">
              Real HP and glitch spends only stick if you sync them to the actual character sheets.
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              <label className="flex items-center gap-2 border-2 border-[color:var(--border-hard)] px-2 py-1 cursor-pointer">
                <input
                  data-testid="sync-hp-checkbox"
                  type="checkbox"
                  checked={syncHp}
                  onChange={(e) => onToggleSyncHp(e.target.checked)}
                />
                <span className="text-[10px] font-mono uppercase tracking-widest">SYNC HP</span>
              </label>
              <label className="flex items-center gap-2 border-2 border-[color:var(--border-hard)] px-2 py-1 cursor-pointer">
                <input
                  data-testid="sync-glitches-checkbox"
                  type="checkbox"
                  checked={syncGlitches}
                  onChange={(e) => onToggleSyncGlitches(e.target.checked)}
                />
                <span className="text-[10px] font-mono uppercase tracking-widest">SYNC GLITCHES</span>
              </label>
            </div>

            <div className="space-y-1 mb-3">
              {punkChanges.filter((p) => p.changed).map((p) => (
                <div key={p.id} className="border-2 border-[color:var(--border-hard)] px-2 py-1 flex items-center justify-between gap-2" data-testid={`sync-row-${p.id}`}>
                  <div className="font-display uppercase font-black text-sm truncate">{p.name}</div>
                  <div className="flex items-center gap-3 font-mono text-[11px]">
                    {p.hpDelta !== 0 && (
                      <span className={p.hpDelta < 0 ? "text-[color:var(--accent-magenta)]" : "text-[color:var(--accent-cyan)]"}>
                        HP {p.hp_original} → {p.hp_current} ({p.hpDelta > 0 ? "+" : ""}{p.hpDelta})
                      </span>
                    )}
                    {p.glitchDelta !== 0 && (
                      <span className={p.glitchDelta < 0 ? "text-[color:var(--accent-magenta)]" : "text-[color:var(--accent-cyan)]"}>
                        GL {p.glitches_original} → {p.glitches_current} ({p.glitchDelta > 0 ? "+" : ""}{p.glitchDelta})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                data-testid="sync-btn"
                onClick={onSyncPunks}
                disabled={syncing || (!syncHp && !syncGlitches)}
                className="cy-btn cy-btn-solid-yellow text-sm disabled:opacity-50"
              >
                {syncing ? "SYNCING…" : "WRITE TO SHEETS"}
              </button>
              <button data-testid="skip-sync-btn" onClick={onReset} className="cy-btn cy-btn-cyan text-sm">
                SKIP · NEW FIGHT
              </button>
            </div>
          </div>
        )}

        {syncDone && (
          <div className="panel panel-cyan p-3 text-center" data-testid="sync-done">
            <div className="font-display uppercase font-black text-lg">// SHEETS UPDATED</div>
            <div className="font-mono text-xs text-[color:var(--text-secondary)] uppercase tracking-widest mt-1">
              Real HP and glitch counts are written back.
            </div>
          </div>
        )}

        <div className="text-center">
          <button onClick={onReset} data-testid="new-fight-btn" className="cy-btn cy-btn-solid-yellow mt-2">
            <RefreshCw size={14} className="mr-2" /> NEW FIGHT
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)] mb-1">// PUNKS</div>
          <div className="grid grid-cols-1 gap-2">
            {punks.map((c) => (
              <div key={c.id} className="relative">
                <CombatantCard
                  c={c}
                  active={active?.id === c.id}
                  canBeTarget={active?.side === "foe" && c.hp_current > 0}
                  onSelectTarget={onSelectTarget}
                  targeted={target?.id === c.id}
                />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => onEditHp(c.id, -1)} className="cy-btn text-[10px] min-h-0 px-1 py-0 flex-1">HP −</button>
                  <button onClick={() => onEditHp(c.id, 1)} className="cy-btn text-[10px] min-h-0 px-1 py-0 flex-1">HP +</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-magenta)] mb-1">// FOES</div>
          <div className="grid grid-cols-1 gap-2">
            {foes.map((c) => (
              <div key={c.id} className="relative">
                <CombatantCard
                  c={c}
                  active={active?.id === c.id}
                  canBeTarget={active?.side === "punk" && c.hp_current > 0}
                  onSelectTarget={onSelectTarget}
                  targeted={target?.id === c.id}
                />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => onEditHp(c.id, -1)} className="cy-btn text-[10px] min-h-0 px-1 py-0 flex-1">HP −</button>
                  <button onClick={() => onEditHp(c.id, 1)} className="cy-btn text-[10px] min-h-0 px-1 py-0 flex-1">HP +</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {reinforcements.length > 0 && (
        <div className="panel panel-magenta p-2 flex flex-wrap gap-2 items-center" data-testid="reinforcements-banner">
          <Skull size={14} className="text-[color:var(--accent-magenta)]" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-magenta)]">SECOPS INBOUND</span>
          {reinforcements.map((r) => (
            <span key={r.id} data-testid={`reinforcement-${r.id}`} className="tape tape-magenta text-[10px]">
              {r.count}× SECOP · {Math.max(0, r.round - round)} RND{r.round - round === 1 ? "" : "S"} · {r.caller}
            </span>
          ))}
        </div>
      )}

      {/* Turn controls */}
      {active && active.hp_current > 0 && (
        <div className="panel panel-yellow p-3" data-testid="turn-panel">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">// {active.side === "punk" ? "PUNK" : "FOE"} ACTIVE</div>
          <div className="font-display uppercase font-black text-2xl mb-2 flex items-center gap-2 flex-wrap">
            {active.name}
            {active.side === "punk" && active.canJackIn && (
              <span data-testid="jack-status" className={`tape text-[10px] ${active.jackedIn ? "tape-cyan" : "tape-magenta"}`}>
                {active.jackedIn ? `JACKED IN · APPS ONLINE${active.appAdvantage ? " · ADV READY" : ""}` : "DECK OFFLINE"}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            {/* Weapon */}
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] flex items-center gap-1">
                WEAPON {weapon && isAutofire(weapon.damage) && <SprayBadge />}
              </div>
              <select
                data-testid="weapon-select"
                value={weaponIdx}
                onChange={(e) => onSelectWeapon(parseInt(e.target.value, 10))}
                className="cy-input mt-1"
              >
                {active.weapons.map((w, i) => (w.app && !active.jackedIn) ? null : (
                  <option key={i} value={i}>
                    {isAutofire(w.damage) ? "◈ " : ""}{w.name} ({w.damage}) [{w.type}]{isAutofire(w.damage) ? " · SPRAY" : ""}
                  </option>
                ))}
              </select>
              {weapon && isAutofire(weapon.damage) && (
                <div className="flex border-2 border-[color:var(--border-hard)] mt-2" data-testid="fire-mode-toggle">
                  {[
                    { k: true, label: "AUTOFIRE" },
                    { k: false, label: "SINGLE-FIRE" },
                  ].map((m) => (
                    <button
                      key={String(m.k)}
                      data-testid={`fire-mode-${m.k ? "auto" : "single"}`}
                      onClick={() => onToggleAutofire(m.k)}
                      className={`flex-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
                        useAutofire === m.k
                          ? "bg-[color:var(--accent-magenta)] text-[color:var(--bg-void)]"
                          : "text-[color:var(--text-secondary)]"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
              {weapon && isAutofire(weapon.damage) && useAutofire && (
                <div className="mt-2" data-testid="autofire-status">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                    AUTOFIRE — up to 3 shots this turn, DR +1 per shot fired
                  </div>
                  <div className="mt-1 border-2 border-[color:var(--border-hard)] px-2 py-1 flex items-center justify-between">
                    <span className="text-[10px] font-mono uppercase tracking-widest">
                      SHOT <span className="text-[color:var(--accent-yellow)] font-black" data-testid="shot-counter">{shotsThisTurn + 1}</span>/3
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-widest">
                      NEXT DR <span className="text-[color:var(--accent-magenta)] font-black">{dr + shotsThisTurn}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/* Attribute */}
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">ATTRIBUTE</div>
              <select
                data-testid="attr-select"
                value={attrKey}
                onChange={(e) => onSelectAttr(e.target.value)}
                className="cy-input mt-1"
              >
                {STAT_KEYS.map((s) => (
                  <option key={s.k} value={s.k}>{s.label} · {s.hint} ({(active.stats[s.k] || 0) >= 0 ? "+" : ""}{active.stats[s.k] || 0})</option>
                ))}
              </select>
            </div>
            {/* Target */}
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">TARGET</div>
              <div className="cy-input mt-1 flex items-center">
                {target ? (
                  <span className="text-[color:var(--accent-magenta)] font-bold">{target.name}</span>
                ) : (
                  <span className="text-[color:var(--text-muted)]">click TARGET on a combatant →</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="attack-btn"
              onClick={onAttack}
              disabled={!target}
              className="cy-btn cy-btn-solid-yellow text-sm disabled:opacity-50"
            >
              <Swords size={14} className="mr-1" /> {weapon && isAutofire(weapon.damage) && useAutofire ? `FIRE SHOT ${shotsThisTurn + 1}/3` : "ATTACK"} (d20{nextAdvantage ? " ADV" : ""} + {STAT_KEYS.find((s) => s.k === attrKey)?.label} vs DR{weapon && isAutofire(weapon.damage) && useAutofire ? dr + shotsThisTurn : dr})
            </button>
            {shotsThisTurn > 0 && (
              <button
                data-testid="end-turn-btn"
                onClick={onEndTurn}
                className="cy-btn cy-btn-magenta text-sm"
                title="Stop firing this turn — advances to the next combatant"
              >
                END TURN
              </button>
            )}
            {active.side === "punk" && (
              <button
                data-testid="use-glitch-btn"
                onClick={onUseGlitch}
                disabled={(active.glitches_current || 0) <= 0}
                title="Spend 1 glitch to gain ADVANTAGE on your next attack roll"
                className="cy-btn cy-btn-magenta text-sm disabled:opacity-40"
              >
                <Zap size={14} className="mr-1" /> USE GLITCH ({active.glitches_current || 0}/{active.glitches_max || 0})
              </button>
            )}
            {active.side === "punk" && (active.usableGear || []).length > 0 && (
              <div className="relative inline-block" data-testid="use-item-wrap">
                <details className="inline-block">
                  <summary className="cy-btn cy-btn-cyan text-sm cursor-pointer list-none">
                    <FolderOpen size={14} className="inline mr-1" /> USE ITEM ({(active.usableGear || []).length})
                  </summary>
                  <div className="absolute left-0 top-full mt-1 min-w-[240px] panel panel-cyan p-2 z-20" data-testid="use-item-menu">
                    {(active.usableGear || []).map((g) => (
                      <button
                        key={g.id}
                        data-testid={`use-item-${g.id}`}
                        onClick={() => onUseItem(g)}
                        className="w-full text-left border-2 border-[color:var(--border-hard)] px-2 py-1 mb-1 hover:border-[color:var(--accent-cyan)] block"
                      >
                        <div className="font-mono text-xs text-[color:var(--text-primary)] truncate">{g.name}</div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)]">
                          {g.effect.label}{g.effect.die ? ` · ${g.effect.die}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                </details>
              </div>
            )}
            {active.side === "punk" && active.canJackIn && !active.jackedIn && shotsThisTurn === 0 && (
              <button
                data-testid="jack-in-btn"
                onClick={onJackIn}
                title={`Knowledge test d20+KNO vs DR${dr}. Success unlocks apps for the fight; failure alerts d6 SecOps arriving in d4 rounds`}
                className="cy-btn cy-btn-cyan text-sm"
              >
                <Cpu size={14} className="mr-1" /> JACK IN (KNO vs DR{dr} · 1 TURN)
              </button>
            )}
            <button data-testid="pass-btn" onClick={onPass} className="cy-btn cy-btn-cyan text-sm">
              PASS / MOVE
            </button>
          </div>
        </div>
      )}

      {/* If active is down, advance */}
      {active && active.hp_current === 0 && (
        <div className="panel panel-magenta p-3 flex items-center justify-between">
          <div className="font-mono uppercase text-xs">{active.name} IS DOWN — skipping…</div>
          <button data-testid="skip-btn" onClick={onPass} className="cy-btn cy-btn-magenta text-xs">SKIP</button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// LOG FORMATTER
// ============================================================
function formatLogLine(r) {
  const head = `${r.attacker} → ${r.target}`;
  const shotTag = r.shotsTotal && r.shotsTotal > 1 ? `[SHOT ${r.shotIndex}/${r.shotsTotal} · DR${r.dr}] ` : "";
  const advTag = r.attack?.advantage ? ` ADV[${r.attack.rolls.join(",")}]` : "";
  if (r.outcome === "fumble") {
    return `${shotTag}[FUMBLE] ${head} · d20=1${advTag}  ⟶  ${r.fumble.table} ${r.fumble.die}=${r.fumble.roll}\n  ${r.fumble.effect}`;
  }
  if (r.outcome === "crit") {
    return `${shotTag}[CRIT] ${head} · d20=20${advTag}  ⟶  ${r.weapon} (${r.weaponDamage}) MAX(${r.damageRoll.max}) + rolled(${r.damageRoll.total - r.damageRoll.max}) = ${r.damageRoll.total} dmg\n  HP ${r.hp_before} → ${r.hp_after}${r.broken ? "  ✕ BROKEN" : ""}`;
  }
  if (r.outcome === "miss") {
    return `${shotTag}[MISS] ${head} · d20=${r.attack.nat}${advTag}${r.attack.mod >= 0 ? "+" : ""}${r.attack.mod} = ${r.attack.total} vs DR${r.dr}`;
  }
  if (r.outcome === "dodged") {
    return `${shotTag}[DODGED] ${head} · attack hit (${r.attack.total}${advTag}) but ${r.target} rolled AGI d20=${r.dodgeRoll.nat}${r.dodgeRoll.mod >= 0 ? "+" : ""}${r.dodgeRoll.mod} = ${r.dodgeRoll.total}`;
  }
  // hit
  const armor = r.armorRoll ? ` − d${r.armorRoll.die}=${r.armorRoll.roll}` : "";
  const notes = r.damageRoll?.notes ? `  (${r.damageRoll.notes})` : "";
  return `${shotTag}[HIT] ${head} · d20=${r.attack.nat}${advTag}${r.attack.mod >= 0 ? "+" : ""}${r.attack.mod} = ${r.attack.total} vs DR${r.dr}\n  ${r.weapon} (${r.weaponDamage}) = ${r.damageRoll.total}${armor} = ${r.damageAfterArmor} dmg${notes}\n  HP ${r.hp_before} → ${r.hp_after}${r.broken ? "  ✕ BROKEN" : ""}`;
}
